import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';

/**
 * MeetingStateContext — Late Joiner State Sync
 *
 * Maintains a server-side snapshot of all meeting state and syncs it
 * to every participant via Socket.IO. Late joiners fetch the snapshot
 * via REST on connect, then receive real-time updates through events.
 *
 * State categories synced:
 * - Chat message history
 * - Active scene & layout
 * - Recording status
 * - Host controls (mute all, lock, etc.)
 * - Active elements/overlays (CTA, timer, presentation, etc.)
 * - Raised hands queue
 * - Live streaming status
 * - Meeting metadata (start time, session name, type)
 */

const MeetingStateContext = createContext(null);

export function MeetingStateProvider({
  children,
  meetingId,
  participantId,
  participantName,
  isHost = false,
  sessionName = '',
  sessionType = 'meeting',
  onMeetingEnded,
}) {
  const [isConnected, setIsConnected] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [isSnapshotLoaded, setIsSnapshotLoaded] = useState(false);

  // Incremental state from real-time events
  const [raisedHands, setRaisedHands] = useState([]);
  const [activeElements, setActiveElements] = useState([]);
  const [activeSceneId, setActiveSceneId] = useState('scene-1');
  const [activeLayout, setActiveLayout] = useState('gallery');
  const [recording, setRecording] = useState({ isRecording: false, recordingMode: null, startedAt: null, paused: false, pausedAt: null });
  const [notetaker, setNotetaker] = useState({ isActive: false, startedAt: null });
  // Privacy mode flag — backed by Meeting.privacy on the server. When on,
  // recording / AI Notetaker / AI Notes are blocked at the API boundary and the
  // UI shows the PrivacyNoticeModal instead of the usual triggers.
  const [privacy, setPrivacy] = useState(false);
  // isRoom — does the current Meeting document have isRoom=true? Only rooms
  // expose Privacy Mode (server gate refuses privacy:true on non-rooms),
  // so the in-meeting Privacy toggle hides itself on scheduled meetings.
  // Populated from the snapshot in `meeting-state:privacy-changed`.
  const [isRoom, setIsRoom] = useState(false);
  const [hostControls, setHostControls] = useState({ muteAll: false, meetingLocked: false, waitingRoomEnabled: false, guestCanMute: true });
  const [chatHistory, setChatHistory] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [meetingStartedAt, setMeetingStartedAt] = useState(null);
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [spotlightedParticipants, setSpotlightedParticipants] = useState([]);
  const [categoryAssignments, setCategoryAssignments] = useState({});
  // Webinar Start Webinar button state — set true once a host clicks the
  // button. Hosts can enter early to prep; auto-record / auto-livestream /
  // auto-admit only fire after this flips true. Synced via meeting state
  // socket so all hosts/co-hosts see the same state.
  const [webinarStarted, setWebinarStarted] = useState(false);
  const [webinarStartedAt, setWebinarStartedAt] = useState(null);
  const [isMeetingEnded, setIsMeetingEnded] = useState(false);
  const [isForceDisconnected, setIsForceDisconnected] = useState(false);
  // Socket.IO chat messages received via broadcast (fallback for Agora Chat)
  const [socketChatMessages, setSocketChatMessages] = useState([]);

  // Meeting-wide branding + per-participant branding, persisted on the
  // server so rejoining guests hydrate from the REST snapshot even if the
  // host's RTM re-publish races with their join.
  const [snapshotBranding, setSnapshotBranding] = useState(null);
  const [snapshotParticipantBranding, setSnapshotParticipantBranding] = useState(null);

  // Whiteboard state — redundant channel alongside RTM PubSub. Socket.IO is
  // more reliable for long sessions (RTM tokens/persist drift after ~1h) and
  // for late joiners and participants returning from breakout rooms.
  const [whiteboardState, setWhiteboardState] = useState({ isOpen: false, roomUUID: null, openedAt: null });

  // Transcript / AI Notetaker state
  const [meetingNotes, setMeetingNotes] = useState(null);
  const [notesProcessing, setNotesProcessing] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const socketRef = useRef(null);
  // Callback refs for mute events — set by MeetingContainerNew where mMeeting is available
  const onForceMutedRef = useRef(null);
  const onForceMuteAllRef = useRef(null);
  const onUnmuteGrantedRef = useRef(null);
  const onUnmuteGrantedAllRef = useRef(null);
  const onSpotlightChangedRef = useRef(null);
  const onHostRewardEarnedRef = useRef(null);
  const onBroadcastMessageRef = useRef(null);

  // --- Contract real-time sync (ephemeral) ---
  // Subscribers keyed by contractId so multiple renderers can listen
  // independently. The subscribers Map lives on a ref so useEffect
  // cleanup can remove a single listener without re-registering.
  const contractSubscribersRef = useRef(new Map());

  // --- Presentation real-time sync (Apryse) ---
  // Subscribers keyed by presentationId, mirroring the contract pattern.
  const presentationSubscribersRef = useRef(new Map());

  // ─── Fetch snapshot via REST on mount ───
  useEffect(() => {
    if (!meetingId) return;

    const fetchSnapshot = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL || '';
        const response = await axios.get(`${baseUrl}/agora/meeting-state/${meetingId}`);
        const data = response.data;

        if (data.exists && data.snapshot) {
          const s = data.snapshot;
          setSnapshot(s);
          setChatHistory(s.chatMessages || []);
          setPinnedMessages(s.pinnedMessages || []);
          setActiveSceneId(s.activeSceneId || 'scene-1');
          setActiveLayout(s.activeLayout || 'gallery');
          setRecording(s.recording || { isRecording: false, recordingMode: null, startedAt: null, paused: false, pausedAt: null });
          setNotetaker(s.notetaker || { isActive: false, startedAt: null });
          setHostControls(s.hostControls || { muteAll: false, meetingLocked: false, waitingRoomEnabled: false, guestCanMute: true });
          setActiveElements(s.activeElements || []);
          setRaisedHands(s.raisedHands || []);
          setMeetingStartedAt(s.meetingStartedAt || null);
          setIsLiveStreaming(s.isLiveStreaming || false);
          setSpotlightedParticipants(s.spotlightedParticipants || []);
          setWebinarStarted(!!s.webinarStarted);
          setWebinarStartedAt(s.webinarStartedAt || null);
          // Hydrate persisted branding for rejoining guests.  Leave null if
          // the server has no branding yet so the host's RTM publish (which
          // arrives a moment later) isn't clobbered by a stale empty object.
          if (s.branding && Object.keys(s.branding).length > 0) {
            setSnapshotBranding(s.branding);
          }
          if (s.participantBranding && Object.keys(s.participantBranding).length > 0) {
            setSnapshotParticipantBranding(s.participantBranding);
          }
          if (s.whiteboard && s.whiteboard.isOpen && s.whiteboard.roomUUID) {
            setWhiteboardState({
              isOpen: true,
              roomUUID: s.whiteboard.roomUUID,
              openedAt: s.whiteboard.openedAt || null,
            });
          }
          console.log('[MeetingState] Snapshot loaded:', Object.keys(s));
        }
      } catch (error) {
        console.error('[MeetingState] Failed to fetch snapshot:', error.message);
      } finally {
        setIsSnapshotLoaded(true);
      }
    };

    fetchSnapshot();
  }, [meetingId]);

  // ─── Socket.IO connection for real-time state updates ───
  useEffect(() => {
    if (!meetingId || !participantId) return;

    const baseUrl = process.env.REACT_APP_BASE_URL?.replace('/api', '') || '';
    const isRecorder = new URLSearchParams(window.location.search).get('recorder') === 'true';
    const socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      query: isRecorder ? { recorder: 'true' } : {},
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[MeetingState] Socket connected');
      setIsConnected(true);

      // Join the meeting state room
      socket.emit('meeting-state:join', {
        meetingId,
        participantId,
        participantName,
        isHost,
        sessionName,
        sessionType,
      });
    });

    socket.on('disconnect', () => {
      console.log('[MeetingState] Socket disconnected');
      setIsConnected(false);
    });

    // ─── Real-time event listeners ───

    socket.on('meeting-state:started-at', (data) => {
      if (data.meetingStartedAt && !meetingStartedAt) {
        console.log('[MeetingState] Received meetingStartedAt:', data.meetingStartedAt);
        setMeetingStartedAt(data.meetingStartedAt);
      }
    });

    socket.on('meeting-state:scene-changed', (data) => {
      setActiveSceneId(data.sceneId);
      if (data.layout) setActiveLayout(data.layout);
    });

    socket.on('meeting-state:recording-changed', (data) => {
      setRecording(data);
    });

    socket.on('meeting-state:notetaker-changed', (data) => {
      setNotetaker(data);
    });

    // Server emits this on socket join AND on demand when the client calls
    // 'meeting-state:chat-history-request'. Payload: { messages, count, source }.
    // Merge into chatHistory (dedup by id, sort by timestamp). This is the
    // backup channel that ensures every client has the complete chat even
    // if the REST snapshot raced or some messages came in via Agora Chat
    // outside the socket broadcast path.
    socket.on('meeting-state:chat-history-sync', (data) => {
      const incoming = Array.isArray(data?.messages) ? data.messages : [];
      if (incoming.length === 0) return;
      setChatHistory(prev => {
        const seen = new Set(prev.map((m) => m?.id).filter(Boolean));
        let changed = false;
        const merged = [...prev];
        for (const m of incoming) {
          if (m?.id && !seen.has(m.id)) {
            merged.push(m);
            seen.add(m.id);
            changed = true;
          }
        }
        if (!changed) return prev;
        merged.sort((a, b) => (a?.timestamp || 0) - (b?.timestamp || 0));
        return merged;
      });
    });

    socket.on('meeting-state:privacy-changed', (data) => {
      setPrivacy(!!data?.privacy);
      // Snapshot emit includes isRoom; live toggle emits don't. Only
      // overwrite when present so live toggles don't reset to false.
      if (typeof data?.isRoom === 'boolean') setIsRoom(data.isRoom);
    });

    socket.on('meeting-state:host-controls-changed', (data) => {
      setHostControls(data);
    });

    // Late-joiner branding delivery via socket (server-persisted fallback
    // for the RTM broadcast which may race with the joiner's RTM handshake).
    socket.on('meeting-state:branding-changed', (data) => {
      if (data && typeof data === 'object') {
        setSnapshotBranding(prev => ({ ...(prev || {}), ...data }));
      }
    });
    socket.on('meeting-state:participant-branding-changed', (data) => {
      if (!data || data.participantId == null) return;
      setSnapshotParticipantBranding(prev => {
        const next = { ...(prev || {}) };
        if (data.branding) {
          next[data.participantId] = data.branding;
        } else {
          delete next[data.participantId];
        }
        return next;
      });
    });

    socket.on('meeting-state:whiteboard-changed', (data) => {
      if (!data) return;
      setWhiteboardState({
        isOpen: !!data.isOpen,
        roomUUID: data.roomUUID || null,
        openedAt: data.openedAt || null,
      });
    });

    socket.on('meeting-state:element-added', (element) => {
      if (element._replace) {
        // Atomic replace: only this element should be active
        setActiveElements([element]);
      } else {
        setActiveElements(prev => {
          const filtered = prev.filter(e => e.id !== element.id);
          return [...filtered, element];
        });
      }
    });

    // --- Contract real-time sync dispatch ---
    socket.on('meeting-state:contract-event', (msg) => {
      const { contractId } = msg || {};
      if (!contractId) return;
      const subs = contractSubscribersRef.current.get(contractId);
      if (!subs || subs.size === 0) return;
      subs.forEach((fn) => {
        try {
          fn(msg);
        } catch (err) {
          console.warn('[MeetingState] contract-event subscriber threw', err);
        }
      });
    });

    // --- Presentation real-time sync dispatch ---
    socket.on('meeting-state:presentation-event', (msg) => {
      const { presentationId } = msg || {};
      if (!presentationId) return;
      const subs = presentationSubscribersRef.current.get(presentationId);
      if (!subs || subs.size === 0) return;
      subs.forEach((fn) => {
        try {
          fn(msg);
        } catch (err) {
          console.warn('[MeetingState] presentation-event subscriber threw', err);
        }
      });
    });

    socket.on('meeting-state:element-removed', ({ elementId }) => {
      setActiveElements(prev => prev.filter(e => e.id !== elementId));
    });

    socket.on('meeting-state:element-updated', ({ elementId, config }) => {
      setActiveElements(prev => prev.map(e =>
        e.id === elementId ? { ...e, config: { ...e.config, ...config } } : e
      ));
    });

    socket.on('meeting-state:hand-raised', (data) => {
      setRaisedHands(prev => {
        if (prev.find(h => h.participantId === data.participantId)) return prev;
        return [...prev, data];
      });
    });

    socket.on('meeting-state:hand-lowered', ({ participantId: pid }) => {
      setRaisedHands(prev => prev.filter(h => h.participantId !== pid));
    });

    socket.on('meeting-state:hands-all-lowered', () => {
      setRaisedHands([]);
    });

    socket.on('meeting-state:livestream-changed', ({ isLiveStreaming: live }) => {
      setIsLiveStreaming(live);
    });

    socket.on('meeting-state:webinar-started', ({ webinarStartedAt: startedAt } = {}) => {
      setWebinarStarted(true);
      setWebinarStartedAt(startedAt || Date.now());
    });

    // --- Mute events (Socket.IO-based for reliability) ---
    socket.on('meeting-state:force-muted', (data) => {
      console.log('[MeetingState] Force mute received:', data);
      onForceMutedRef.current?.(data);
    });

    socket.on('meeting-state:force-mute-all', (data) => {
      console.log('[MeetingState] Force mute ALL received:', data);
      onForceMuteAllRef.current?.(data);
    });

    socket.on('meeting-state:unmute-granted', (data) => {
      console.log('[MeetingState] Unmute granted received:', data);
      onUnmuteGrantedRef.current?.(data);
    });

    socket.on('meeting-state:unmute-granted-all', () => {
      console.log('[MeetingState] Unmute granted ALL received');
      onUnmuteGrantedAllRef.current?.();
    });

    // --- Spotlight events ---
    socket.on('meeting-state:spotlight-changed', (data) => {
      console.log('[MeetingState] Spotlight changed:', data);
      setSpotlightedParticipants(data.spotlightedParticipants || []);
      onSpotlightChangedRef.current?.(data);
    });

    socket.on('meeting-state:spotlight-sync', (data) => {
      console.log('[MeetingState] Spotlight sync (late join):', data);
      setSpotlightedParticipants(data.spotlightedParticipants || []);
      // Replay each spotlight so MeetingContainerNew pins them
      (data.spotlightedParticipants || []).forEach((s) => {
        onSpotlightChangedRef.current?.({
          participantId: s.participantId,
          displayName: s.displayName,
          action: 'spotlight',
        });
      });
    });

    // --- Category assignment events ---
    socket.on('meeting-state:category-assigned', (data) => {
      const { participantId: pid, category } = data;
      setCategoryAssignments(prev => {
        const next = { ...prev };
        if (category) {
          next[pid] = category;
        } else {
          delete next[pid];
        }
        return next;
      });
    });

    socket.on('meeting-state:category-sync', (data) => {
      if (data.categoryAssignments) {
        setCategoryAssignments(data.categoryAssignments);
      }
    });

    // --- Host reward event (real-time unique-IP detection) ---
    socket.on('meeting-state:host-reward-earned', (data) => {
      console.log('[MeetingState] Host reward earned:', data);
      onHostRewardEarnedRef.current?.(data);
    });

    // --- Host broadcast message received ---
    socket.on('meeting-state:broadcast-message', (data) => {
      console.log('[MeetingState] Broadcast message received:', data);
      onBroadcastMessageRef.current?.(data);
    });

    // --- Socket.IO chat message broadcast (parallel delivery alongside Agora Chat) ---
    socket.on('meeting-state:chat-message-broadcast', (data) => {
      if (!data?.message) return;
      const msg = data.message;
      // Skip messages from self (sender already has it via optimistic local add)
      if (msg.userId === participantId || msg.userId === String(participantId)) return;

      // Add to chatHistory (dedup by message ID). No size cap — we keep the
      // entire meeting chat so panel reopens and late joiners always see
      // every message. Long meetings with thousands of messages are still
      // cheap (a chat message is small JSON; React renders are virtualized
      // by the panel's overflow scroll).
      setChatHistory(prev => {
        if (prev.some(existing => existing.id === msg.id)) return prev;
        return [...prev, msg];
      });

      // Also track in separate socketChatMessages for ChatPanel to merge
      setSocketChatMessages(prev => {
        if (prev.some(existing => existing.id === msg.id)) return prev;
        return [...prev, msg];
      });
    });

    // --- Transcript / AI Notetaker events ---
    socket.on('transcript:notes-ready', (data) => {
      console.log('[MeetingState] Meeting notes ready:', data.meetingId);
      setMeetingNotes(data.notes);
      setNotesProcessing(false);
      setNotesError(null);
    });

    socket.on('transcript:notes-processing', () => {
      console.log('[MeetingState] Notes processing...');
      setNotesProcessing(true);
      setNotesError(null);
    });

    socket.on('transcript:notes-error', (data) => {
      console.log('[MeetingState] Notes error:', data.error);
      setNotesProcessing(false);
      setNotesError(data.error);
    });

    // Force-disconnect: another device joined with the same login
    socket.on('meeting-state:force-disconnect', (data) => {
      console.log('[MeetingState] Force-disconnected: joined from another device', data);
      setIsForceDisconnected(true);
    });

    socket.on('meeting-state:ended', (data) => {
      // webinarEnd flag: the host clicked "End Webinar" — participants get
      // booted but hosts/co-hosts stay in the room. Without the flag
      // (normal "End Session for All") everyone leaves.
      if (data?.webinarEnd && isHost) {
        console.log('[MeetingState] Webinar ended — staying as host/co-host');
        return; // hosts stay
      }
      console.log('[MeetingState] Meeting ended by host');
      setSnapshot(null);
      setChatHistory([]);
      setPinnedMessages([]);
      setRaisedHands([]);
      setActiveElements([]);
      setCategoryAssignments({});
      setIsMeetingEnded(true);
      if (onMeetingEnded) {
        onMeetingEnded();
      }
    });

    socket.on('meeting-state:selective-kick', (data) => {
      // Expose the event data so MeetingContainerNew can check against Agora UIDs
      window.dispatchEvent(new CustomEvent('rlink-selective-kick', { detail: data }));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [meetingId, participantId, participantName, isHost, sessionName, sessionType]);

  // ─── Emit helpers (called by UI components to update state) ───

  const emitChatMessage = useCallback((message) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:chat-message', { meetingId, message });
    // Also add locally — no size cap; full meeting history is preserved.
    setChatHistory(prev => {
      if (message?.id && prev.some((existing) => existing.id === message.id)) return prev;
      return [...prev, message];
    });
  }, [meetingId]);

  // Public helper: ask the server to re-emit the full chat history. Used as
  // a backup check when the chat panel reopens, the tab becomes visible
  // again, or the socket reconnects. The server's response merges into
  // chatHistory via the 'meeting-state:chat-history-sync' listener (no
  // duplicates, sorted by timestamp). Safe to call repeatedly.
  const requestChatHistorySync = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:chat-history-request', { meetingId });
  }, [meetingId]);

  // Backup: re-fetch the REST snapshot on visibility resume and on socket
  // reconnect. Even though chat-history-sync covers most cases, the REST
  // path is independent of socket health and provides a final safety net.
  useEffect(() => {
    if (!meetingId) return;
    const refetch = () => {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      axios.get(`${baseUrl}/agora/meeting-state/${meetingId}`).then((response) => {
        const s = response?.data?.snapshot;
        if (!s || !Array.isArray(s.chatMessages) || s.chatMessages.length === 0) return;
        setChatHistory(prev => {
          const seen = new Set(prev.map((m) => m?.id).filter(Boolean));
          let changed = false;
          const merged = [...prev];
          for (const m of s.chatMessages) {
            if (m?.id && !seen.has(m.id)) {
              merged.push(m);
              seen.add(m.id);
              changed = true;
            }
          }
          if (!changed) return prev;
          merged.sort((a, b) => (a?.timestamp || 0) - (b?.timestamp || 0));
          return merged;
        });
      }).catch(() => { /* ignore — best-effort backup */ });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Pull fresh history when the user returns to the tab. Also ask the
        // socket for a sync — both paths feed the same dedup'd state.
        refetch();
        if (socketRef.current?.connected) {
          socketRef.current.emit('meeting-state:chat-history-request', { meetingId });
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [meetingId]);

  const emitPinMessage = useCallback((messageId, isPinned) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:pin-message', { meetingId, messageId, isPinned });
  }, [meetingId]);

  const emitDeleteMessage = useCallback((messageId) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:delete-message', { meetingId, messageId });
  }, [meetingId]);

  const emitMessageReaction = useCallback((messageId, emoji, userId, action) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:message-reaction', { meetingId, messageId, emoji, userId, action });
  }, [meetingId]);

  const emitSceneChange = useCallback((sceneId, layout) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:scene-change', { meetingId, sceneId, layout });
    setActiveSceneId(sceneId);
    if (layout) setActiveLayout(layout);
  }, [meetingId]);

  const emitRecordingUpdate = useCallback((isRecording, recordingMode, paused = false) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:recording-update', { meetingId, isRecording, recordingMode, paused });
    setRecording((prev) => ({
      isRecording,
      recordingMode: recordingMode || null,
      // Preserve startedAt across pause/resume so the timer keeps accumulating
      startedAt: isRecording ? (prev.startedAt || Date.now()) : null,
      paused: !!paused,
      pausedAt: paused ? Date.now() : null,
    }));
  }, [meetingId]);

  // Pause / resume the active cloud recording (web mode only — backend rejects mix mode).
  // POSTs to the REST endpoint, then broadcasts the new paused state via Socket.IO so
  // every participant's UI converges instantly without waiting for the next status poll.
  const emitRecordingPause = useCallback(async () => {
    if (!meetingId) return { success: false };
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const resp = await axios.post(`${baseUrl}/recording/pause`, { meetingId });
      if (resp.data?.success) {
        socketRef.current?.emit('meeting-state:recording-update', { meetingId, isRecording: true, recordingMode: resp.data.recordingMode || 'web', paused: true });
        setRecording((prev) => ({ ...prev, paused: true, pausedAt: Date.now() }));
      }
      return resp.data;
    } catch (err) {
      console.error('[Recording] Pause failed:', err.response?.data || err.message);
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, [meetingId]);

  const emitRecordingResume = useCallback(async () => {
    if (!meetingId) return { success: false };
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const resp = await axios.post(`${baseUrl}/recording/resume`, { meetingId });
      if (resp.data?.success) {
        socketRef.current?.emit('meeting-state:recording-update', { meetingId, isRecording: true, recordingMode: resp.data.recordingMode || 'web', paused: false });
        setRecording((prev) => ({ ...prev, paused: false, pausedAt: null }));
      }
      return resp.data;
    } catch (err) {
      console.error('[Recording] Resume failed:', err.response?.data || err.message);
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, [meetingId]);

  const emitNotetakerUpdate = useCallback((isActive) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:notetaker-update', { meetingId, isActive });
    setNotetaker({
      isActive,
      startedAt: isActive ? Date.now() : null,
    });
  }, [meetingId]);

  // Toggle privacy mode for the live meeting. The server persists the flag on
  // the Meeting document and broadcasts `meeting-state:privacy-changed` so all
  // participants converge. We optimistically update local state for snappy UI.
  const emitPrivacyUpdate = useCallback((nextPrivacy) => {
    if (!socketRef.current || !meetingId) return;
    const value = !!nextPrivacy;
    socketRef.current.emit('meeting-state:privacy-update', { meetingId, privacy: value });
    setPrivacy(value);
  }, [meetingId]);

  const emitHostControlsUpdate = useCallback((controls) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:host-controls-update', { meetingId, controls });
    setHostControls(prev => ({ ...prev, ...controls }));
  }, [meetingId]);

  // Persist meeting-wide branding so rejoining guests pick it up via the
  // REST snapshot.  The RTM broadcast still happens separately (and remains
  // the primary real-time delivery path); this socket emit is the durable
  // store + fallback for clients whose RTM hasn't finished connecting yet.
  const emitBrandingUpdate = useCallback((brandingPayload) => {
    if (!socketRef.current || !meetingId || !brandingPayload) return;
    socketRef.current.emit('meeting-state:branding-update', { meetingId, branding: brandingPayload });
  }, [meetingId]);

  const emitParticipantBrandingUpdate = useCallback((participantId, brandingPayload) => {
    if (!socketRef.current || !meetingId || participantId == null) return;
    socketRef.current.emit('meeting-state:participant-branding-update', {
      meetingId,
      participantId,
      branding: brandingPayload || null,
    });
  }, [meetingId]);

  // Whiteboard open/close — redundant with the RTM PubSub "WHITEBOARD"
  // channel. Socket.IO is more reliable for long sessions and breakout return.
  const emitWhiteboardOpen = useCallback((roomUUID) => {
    if (!socketRef.current || !meetingId || !roomUUID) return;
    socketRef.current.emit('meeting-state:whiteboard-open', { meetingId, roomUUID });
  }, [meetingId]);

  const emitWhiteboardClose = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:whiteboard-close', { meetingId });
  }, [meetingId]);

  const emitElementAdd = useCallback((element) => {
    if (!socketRef.current || !meetingId) return;
    // Send replace flag so backend replaces all elements atomically (no race condition)
    socketRef.current.emit('meeting-state:element-add', { meetingId, element, replace: true });
    // Locally replace all elements with just this one
    setActiveElements([element]);
  }, [meetingId]);

  const emitElementRemove = useCallback((elementId) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:element-remove', { meetingId, elementId });
    setActiveElements(prev => prev.filter(e => e.id !== elementId));
  }, [meetingId]);

  const emitElementUpdate = useCallback((elementId, config) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:element-update', { meetingId, elementId, config });
    setActiveElements(prev => prev.map(e =>
      e.id === elementId ? { ...e, config: { ...e.config, ...config } } : e
    ));
  }, [meetingId]);

  const emitHandRaise = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:hand-raise', { meetingId, participantId, participantName });
    setRaisedHands(prev => {
      if (prev.find(h => h.participantId === participantId)) return prev;
      return [...prev, { participantId, participantName, raisedAt: Date.now() }];
    });
  }, [meetingId, participantId, participantName]);

  const emitHandLower = useCallback((targetParticipantId) => {
    if (!socketRef.current || !meetingId) return;
    const pid = targetParticipantId || participantId;
    socketRef.current.emit('meeting-state:hand-lower', { meetingId, participantId: pid });
    setRaisedHands(prev => prev.filter(h => h.participantId !== pid));
  }, [meetingId, participantId]);

  const emitHandsLowerAll = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:hands-lower-all', { meetingId });
    setRaisedHands([]);
  }, [meetingId]);

  const emitLivestreamUpdate = useCallback((streaming) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:livestream-update', { meetingId, isLiveStreaming: streaming });
    setIsLiveStreaming(streaming);
  }, [meetingId]);

  // Webinar host clicks "Start Webinar" — flips the meeting state flag and
  // broadcasts to every host/co-host so the button hides everywhere.
  // Caller (TopBar) is responsible for the side-effects (auto-record,
  // livestream, admit-all) since those touch local Agora SDK state.
  const emitWebinarStart = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:webinar-start', { meetingId });
    setWebinarStarted(true);
    setWebinarStartedAt(Date.now());
  }, [meetingId]);

  const emitSessionUpdate = useCallback((updates) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:session-update', { meetingId, ...updates });
  }, [meetingId]);

  const emitMeetingEnd = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:end', { meetingId });
  }, [meetingId]);

  // End-webinar: boots non-host participants but hosts/co-hosts stay.
  // Also triggers recording stop + replay email flow on the backend.
  const emitEndWebinar = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:end-webinar', {
      meetingId,
      sessionId: snapshot?.sessionId || null,
      hostName: participantName || '',
      meetingTitle: sessionName || '',
      userId: participantId || '',
    });
  }, [meetingId, snapshot?.sessionId, participantName, sessionName, participantId]);

  // --- Contract real-time sync emit/subscribe ---
  const emitContractEvent = useCallback(
    ({ contractId, type, payload }) => {
      if (!socketRef.current || !meetingId || !contractId || !type) return;
      socketRef.current.emit('meeting-state:contract-event', {
        meetingId,
        contractId,
        type,
        payload,
      });
    },
    [meetingId]
  );

  const onContractEvent = useCallback((contractId, handler) => {
    if (!contractId || typeof handler !== 'function') return () => {};
    const map = contractSubscribersRef.current;
    const set = map.get(contractId) || new Set();
    set.add(handler);
    map.set(contractId, set);
    return () => {
      const s = contractSubscribersRef.current.get(contractId);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) contractSubscribersRef.current.delete(contractId);
    };
  }, []);

  // --- Presentation real-time sync emit/subscribe (Apryse) ---
  const emitPresentationEvent = useCallback(
    ({ presentationId, type, payload }) => {
      if (!socketRef.current || !meetingId || !presentationId || !type) return;
      socketRef.current.emit('meeting-state:presentation-event', {
        meetingId,
        presentationId,
        type,
        payload,
      });
    },
    [meetingId]
  );

  const onPresentationEvent = useCallback((presentationId, handler) => {
    if (!presentationId || typeof handler !== 'function') return () => {};
    const map = presentationSubscribersRef.current;
    const set = map.get(presentationId) || new Set();
    set.add(handler);
    map.set(presentationId, set);
    return () => {
      const s = presentationSubscribersRef.current.get(presentationId);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) presentationSubscribersRef.current.delete(presentationId);
    };
  }, []);

  // --- Mute emit functions (Socket.IO-based for reliability) ---
  // Returns a promise that resolves with { success, verified, timedOut } so
  // hosts can show real feedback ("Muted" / "Failed — silenced locally") on
  // flaky-network participants instead of fire-and-forget guessing.
  // Backward-compatible: existing callers that ignore the return value work
  // unchanged, and legacy clients without the ack handler still get muted.
  const emitForceMute = useCallback((targetParticipantId, options = {}) => {
    if (!socketRef.current || !meetingId) {
      return Promise.resolve({ success: false, verified: false, reason: 'no-socket' });
    }
    const socket = socketRef.current;
    const timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : 4000;
    // Use crypto.randomUUID when available (all modern browsers); fall back to
    // a timestamp+random combo for older environments.
    const requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `fm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    return new Promise((resolve) => {
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        socket.off('meeting-state:force-muted-ack', onAck);
        resolve(result);
      };
      const onAck = (data) => {
        if (!data || data.requestId !== requestId) return;
        finish({
          success: !!data.success,
          verified: !!data.verified,
          timedOut: false,
          reason: data.reason || null,
        });
      };
      const timer = setTimeout(() => {
        finish({ success: false, verified: false, timedOut: true, reason: 'ack-timeout' });
      }, timeoutMs);

      socket.on('meeting-state:force-muted-ack', onAck);
      socket.emit('meeting-state:force-mute', {
        meetingId,
        targetParticipantId,
        senderName: participantName,
        requestId,
      });
    });
  }, [meetingId, participantName]);

  // Sent by the target client after processing a force-mute. The backend
  // forwards this to the originating host socket (matched by requestId) so
  // the host UI can show real mute feedback.
  const emitForceMutedAck = useCallback(({ requestId, senderSocketId, success, verified, reason }) => {
    if (!socketRef.current || !requestId || !senderSocketId) return;
    socketRef.current.emit('meeting-state:force-muted-ack', {
      requestId,
      senderSocketId,
      success: !!success,
      verified: !!verified,
      reason: reason || null,
    });
  }, []);

  const emitForceMuteAll = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:force-mute-all', {
      meetingId,
      senderName: participantName,
      senderParticipantId: participantId,
    });
    // Optimistic local update — don't wait for server broadcast
    setHostControls(prev => ({ ...prev, muteAll: true }));
  }, [meetingId, participantName, participantId]);

  const emitGrantUnmute = useCallback((targetParticipantId) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:grant-unmute', {
      meetingId,
      targetParticipantId,
    });
  }, [meetingId]);

  const emitGrantUnmuteAll = useCallback(() => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:grant-unmute-all', { meetingId });
    // Optimistic local update — don't wait for server broadcast
    setHostControls(prev => ({ ...prev, muteAll: false }));
  }, [meetingId]);

  // --- Transcript / AI Notetaker emit functions ---

  const emitTranscriptSegment = useCallback((data) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('transcript:segment', { meetingId, ...data });
  }, [meetingId]);

  const emitSpeakerMap = useCallback((speakerUid, speakerName) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('transcript:speaker-map', { meetingId, speakerUid, speakerName });
  }, [meetingId]);

  const emitTranscriptSetHost = useCallback((hostParticipantId) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('transcript:set-host', { meetingId, hostParticipantId });
  }, [meetingId]);

  const emitTranscriptMeetingEnded = useCallback((sessionId, hostName, meetingTitle, userId) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('transcript:meeting-ended', { meetingId, sessionId, hostName, meetingTitle, userId });
  }, [meetingId]);

  const emitGenerateNotes = useCallback((sessionId, hostName, meetingTitle, userId) => {
    if (!socketRef.current || !meetingId) return;
    setNotesProcessing(true);
    socketRef.current.emit('transcript:generate-notes', { meetingId, sessionId, hostName, meetingTitle, userId });
  }, [meetingId]);

  // Spotlight emit — sends via Socket.IO for persistence + broadcast
  const emitSpotlight = useCallback((targetParticipantId, displayName, action) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:spotlight', {
      meetingId,
      participantId: targetParticipantId,
      displayName: displayName || 'Participant',
      action, // 'spotlight' | 'unspotlight'
    });
  }, [meetingId]);

  // Recording metadata emit functions — track participant/screen share/speaker
  // events for multi-layout recording post-processing
  const emitRecordingMetadataParticipantJoined = useCallback((uid, name) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('recording-metadata:participant-joined', { meetingId, uid: String(uid), name });
  }, [meetingId]);

  const emitRecordingMetadataParticipantLeft = useCallback((uid) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('recording-metadata:participant-left', { meetingId, uid: String(uid) });
  }, [meetingId]);

  const emitRecordingMetadataScreenShareStarted = useCallback((uid, ownerUid, ownerName) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('recording-metadata:screen-share-started', {
      meetingId, uid: String(uid), ownerUid: String(ownerUid), ownerName,
    });
  }, [meetingId]);

  const emitRecordingMetadataScreenShareStopped = useCallback((uid, ownerUid) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('recording-metadata:screen-share-stopped', {
      meetingId, uid: String(uid), ownerUid: String(ownerUid),
    });
  }, [meetingId]);

  const emitRecordingMetadataActiveSpeaker = useCallback((uid, name) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('recording-metadata:active-speaker', { meetingId, uid: String(uid), name });
  }, [meetingId]);

  // Broadcast message emit — sends announcement to all participants via Socket.IO
  const emitBroadcastMessage = useCallback((message, target = 'everyone') => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:broadcast-message', {
      meetingId,
      message,
      senderName: participantName,
      target,
    });
  }, [meetingId, participantName]);

  // Emit category assignment (host assigns category to a participant)
  const emitCategoryAssign = useCallback((targetParticipantId, category) => {
    if (!socketRef.current || !meetingId) return;
    socketRef.current.emit('meeting-state:category-assign', {
      meetingId,
      participantId: targetParticipantId,
      category: category || null,
    });
  }, [meetingId]);

  // Register callbacks for mute/spotlight events (called by MeetingContainerNew)
  const registerMuteCallbacks = useCallback((callbacks) => {
    if (callbacks.onForceMuted) onForceMutedRef.current = callbacks.onForceMuted;
    if (callbacks.onForceMuteAll) onForceMuteAllRef.current = callbacks.onForceMuteAll;
    if (callbacks.onUnmuteGranted) onUnmuteGrantedRef.current = callbacks.onUnmuteGranted;
    if (callbacks.onUnmuteGrantedAll) onUnmuteGrantedAllRef.current = callbacks.onUnmuteGrantedAll;
    if (callbacks.onSpotlightChanged) onSpotlightChangedRef.current = callbacks.onSpotlightChanged;
    if (callbacks.onHostRewardEarned) onHostRewardEarnedRef.current = callbacks.onHostRewardEarned;
    if (callbacks.onBroadcastMessage) onBroadcastMessageRef.current = callbacks.onBroadcastMessage;
  }, []);

  const value = {
    // Raw socket ref (for custom event listeners)
    socket: socketRef.current,

    // Connection state
    isConnected,
    isSnapshotLoaded,

    // Meeting metadata
    meetingStartedAt,
    snapshot,

    // State
    chatHistory,
    socketChatMessages,
    requestChatHistorySync,
    pinnedMessages,
    activeSceneId,
    activeLayout,
    recording,
    notetaker,
    privacy,
    isRoom,
    hostControls,
    activeElements,
    raisedHands,
    isLiveStreaming,
    spotlightedParticipants,
    // Webinar Start Webinar button state
    webinarStarted,
    webinarStartedAt,
    // Meeting ended flag (set when host ends for all)
    isMeetingEnded,
    // Force-disconnected flag (joined from another device)
    isForceDisconnected,
    // Emit functions
    emitChatMessage,
    emitPinMessage,
    emitDeleteMessage,
    emitMessageReaction,
    emitSceneChange,
    emitRecordingUpdate,
    emitRecordingPause,
    emitRecordingResume,
    emitNotetakerUpdate,
    emitPrivacyUpdate,
    emitHostControlsUpdate,
    emitBrandingUpdate,
    emitParticipantBrandingUpdate,
    snapshotBranding,
    snapshotParticipantBranding,
    // Whiteboard (Socket.IO redundant channel alongside RTM PubSub)
    whiteboardState,
    emitWhiteboardOpen,
    emitWhiteboardClose,
    emitElementAdd,
    emitElementRemove,
    emitElementUpdate,
    emitHandRaise,
    emitHandLower,
    emitHandsLowerAll,
    emitLivestreamUpdate,
    emitWebinarStart,
    emitSessionUpdate,
    emitMeetingEnd,
    emitEndWebinar,

    // Mute functions (Socket.IO-based)
    emitForceMute,
    emitForceMuteAll,
    emitForceMutedAck,
    emitGrantUnmute,
    emitGrantUnmuteAll,
    registerMuteCallbacks,

    // Spotlight (Socket.IO-based)
    emitSpotlight,

    // Broadcast message (Socket.IO-based)
    emitBroadcastMessage,

    // Category assignments (shared across all participants)
    categoryAssignments,
    emitCategoryAssign,

    // Recording metadata (for multi-layout post-processing)
    emitRecordingMetadataParticipantJoined,
    emitRecordingMetadataParticipantLeft,
    emitRecordingMetadataScreenShareStarted,
    emitRecordingMetadataScreenShareStopped,
    emitRecordingMetadataActiveSpeaker,

    // Transcript / AI Notetaker
    meetingNotes,
    notesProcessing,
    notesError,
    emitTranscriptSegment,
    emitSpeakerMap,
    emitTranscriptSetHost,
    emitTranscriptMeetingEnded,
    emitGenerateNotes,

    // Contract real-time sync (Apryse)
    emitContractEvent,
    onContractEvent,

    // Presentation real-time sync (Apryse)
    emitPresentationEvent,
    onPresentationEvent,
    connected: isConnected,
  };

  return (
    <MeetingStateContext.Provider value={value}>
      {children}
    </MeetingStateContext.Provider>
  );
}

export function useMeetingState() {
  const context = useContext(MeetingStateContext);
  if (!context) {
    // Return a safe no-op fallback when outside provider
    return {
      isConnected: false,
      isSnapshotLoaded: false,
      meetingStartedAt: null,
      snapshot: null,
      chatHistory: [],
      socketChatMessages: [],
      requestChatHistorySync: () => {},
      pinnedMessages: [],
      activeSceneId: 'scene-1',
      activeLayout: 'gallery',
      recording: { isRecording: false, recordingMode: null, startedAt: null, paused: false, pausedAt: null },
      notetaker: { isActive: false, startedAt: null },
      privacy: false,
      hostControls: { muteAll: false, meetingLocked: false, waitingRoomEnabled: false, guestCanMute: true },
      activeElements: [],
      raisedHands: [],
      isLiveStreaming: false,
      webinarStarted: false,
      webinarStartedAt: null,
      emitWebinarStart: () => {},
      emitChatMessage: () => {},
      emitPinMessage: () => {},
      emitDeleteMessage: () => {},
      emitMessageReaction: () => {},
      emitSceneChange: () => {},
      emitRecordingUpdate: () => {},
      emitRecordingPause: async () => ({ success: false }),
      emitRecordingResume: async () => ({ success: false }),
      emitNotetakerUpdate: () => {},
      emitPrivacyUpdate: () => {},
      emitHostControlsUpdate: () => {},
      emitBrandingUpdate: () => {},
      emitParticipantBrandingUpdate: () => {},
      snapshotBranding: null,
      snapshotParticipantBranding: null,
      emitElementAdd: () => {},
      emitElementRemove: () => {},
      emitElementUpdate: () => {},
      emitHandRaise: () => {},
      emitHandLower: () => {},
      emitHandsLowerAll: () => {},
      emitLivestreamUpdate: () => {},
      emitSessionUpdate: () => {},
      emitMeetingEnd: () => {},
      emitForceMute: () => Promise.resolve({ success: false, verified: false, reason: 'no-provider' }),
      emitForceMuteAll: () => {},
      emitForceMutedAck: () => {},
      emitGrantUnmute: () => {},
      emitGrantUnmuteAll: () => {},
      registerMuteCallbacks: () => {},
      emitSpotlight: () => {},
      emitBroadcastMessage: () => {},
      emitRecordingMetadataParticipantJoined: () => {},
      emitRecordingMetadataParticipantLeft: () => {},
      emitRecordingMetadataScreenShareStarted: () => {},
      emitRecordingMetadataScreenShareStopped: () => {},
      emitRecordingMetadataActiveSpeaker: () => {},
      meetingNotes: null,
      notesProcessing: false,
      notesError: null,
      emitTranscriptSegment: () => {},
      emitSpeakerMap: () => {},
      emitTranscriptSetHost: () => {},
      emitTranscriptMeetingEnded: () => {},
      emitGenerateNotes: () => {},
    };
  }
  return context;
}

export default MeetingStateContext;
